import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null); // {email, name} | false
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) { setInfo(false); return; }
    api.get(`/auth/activation/${token}`)
      .then(({ data }) => { if (active) setInfo(data); })
      .catch(() => { if (active) setInfo(false); });
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Adgangskoden skal være mindst 8 tegn.");
    if (password !== confirm) return setErr("Adgangskoderne matcher ikke.");
    setBusy(true);
    try {
      await api.post("/auth/activate", { token, password });
      setDone(true);
      try { await refresh(); } catch { /* noop */ }
      setTimeout(() => navigate("/profile"), 1800);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center animate-fade-up" data-testid="activate-done-page">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-olive-100 text-olive-600 mx-auto">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-serif text-3xl text-olive-700">Din konto er aktiveret</h1>
        <p className="mt-3 text-brown-600 text-sm">Logger dig ind…</p>
      </div>
    );
  }

  if (info === false) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center animate-fade-up" data-testid="activate-invalid-page">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive mx-auto">
          <AlertCircle className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-serif text-3xl text-olive-700">Ugyldigt aktiveringslink</h1>
        <p className="mt-3 text-brown-600 text-sm">
          Linket er ugyldigt, brugt eller udløbet. Kontakt administratoren for et nyt link.
        </p>
        <Link to="/login" className="btn-primary mt-8 inline-flex" data-testid="activate-go-login">Til log ind</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 animate-fade-up" data-testid="activate-page">
      <div className="eyebrow">Aktivér din konto</div>
      <h1 className="mt-3 font-serif text-4xl text-olive-700">Vælg din adgangskode</h1>
      <p className="mt-3 text-brown-600 text-sm">
        {info ? <>Kontoen <strong>{info.email}</strong> er oprettet til dig. Sæt en adgangskode for at komme i gang.</> : "Indlæser…"}
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm mb-1 text-brown-600">Adgangskode</label>
          <input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="field" placeholder="Mindst 8 tegn" data-testid="activate-password" />
        </div>
        <div>
          <label className="block text-sm mb-1 text-brown-600">Bekræft adgangskode</label>
          <input type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="field" placeholder="••••••••" data-testid="activate-confirm" />
        </div>
        {err && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2" data-testid="activate-error">{err}</div>}
        <button type="submit" disabled={busy || !info} className="btn-primary w-full disabled:opacity-60" data-testid="activate-submit">
          <KeyRound className="h-4 w-4" /> {busy ? "Aktiverer…" : "Aktivér konto"}
        </button>
      </form>
    </div>
  );
}
